import {safetyKnowledgeFallback} from './safety-knowledge.js';

export const DymekEvidenceClass=Object.freeze({
  OBSERVED:'observed',
  VALIDATED_KNOWLEDGE:'validated_knowledge',
  DERIVED_CALCULATION:'derived_calculation',
  GENERAL_SUGGESTION:'general_suggestion'
});
const CLASSES=new Set(Object.values(DymekEvidenceClass));

function required(value,label,max=6000){
  const result=String(value??'').trim();
  if(!result)throw new TypeError(label+' is required');
  if(result.length>max)throw new TypeError(label+' is too long');
  return result;
}
function optional(value,max=1000){
  const result=String(value??'').trim();
  return result?result.slice(0,max):null;
}
function evidenceClass(value){
  const key=String(value??'').trim().toLowerCase();
  if(!CLASSES.has(key))throw new TypeError('Unsupported Dymek evidence class');
  return key;
}
function refs(values=[]){
  if(!Array.isArray(values))throw new TypeError('evidenceRefs must be an array');
  return Object.freeze(values.slice(0,20).map(value=>Object.freeze({
    kind:required(value?.kind,'evidenceRef.kind',60),
    ref:required(value?.ref,'evidenceRef.ref',160),
    observedAt:optional(value?.observedAt,100)
  })));
}
function assumptions(values=[]){
  if(!Array.isArray(values))throw new TypeError('assumptions must be an array');
  return Object.freeze(values.slice(0,20).map(value=>required(value,'assumption',240)));
}

export function createDymekClaim({
  content,
  evidenceClass:klass,
  safetyCritical=false,
  evidenceRefs=[],
  assumptions:claimAssumptions=[],
  historical=false
}={}){
  const resolvedClass=evidenceClass(klass);
  const resolvedRefs=refs(evidenceRefs);
  const resolvedAssumptions=assumptions(claimAssumptions);

  if(historical&&resolvedClass!==DymekEvidenceClass.OBSERVED){
    throw new TypeError('Historical facts must use observed evidence class');
  }
  if(historical&&!resolvedRefs.some(ref=>ref.kind==='session'&&ref.observedAt)){
    throw new TypeError('Historical facts require a dated session evidence reference');
  }
  if(resolvedClass===DymekEvidenceClass.OBSERVED&&resolvedRefs.length===0){
    throw new TypeError('Observed claims require evidence references');
  }
  if(resolvedClass===DymekEvidenceClass.VALIDATED_KNOWLEDGE
    &&resolvedRefs.every(ref=>ref.kind!=='safety_knowledge')){
    throw new TypeError('Validated knowledge claims require safety_knowledge evidence');
  }
  if(resolvedClass===DymekEvidenceClass.DERIVED_CALCULATION
    &&resolvedAssumptions.length===0){
    throw new TypeError('Derived calculations require assumptions');
  }

  return Object.freeze({
    content:required(content,'content'),
    evidenceClass:resolvedClass,
    safetyCritical:Boolean(safetyCritical),
    historical:Boolean(historical),
    evidenceRefs:resolvedRefs,
    assumptions:resolvedAssumptions
  });
}

export function enforceDymekEvidencePolicy(claims=[]){
  if(!Array.isArray(claims))throw new TypeError('claims must be an array');
  const accepted=[];
  const rejected=[];
  for(const raw of claims){
    let claim;
    try{claim=createDymekClaim(raw);}
    catch(error){
      rejected.push(Object.freeze({
        reason:raw?.safetyCritical?'safety_evidence_invalid':'invalid_claim',
        message:String(error?.message||error)
      }));
      continue;
    }
    if(claim.safetyCritical
      &&claim.evidenceClass!==DymekEvidenceClass.VALIDATED_KNOWLEDGE){
      rejected.push(Object.freeze({
        reason:'safety_requires_validated_knowledge',
        evidenceClass:claim.evidenceClass
      }));
      continue;
    }
    accepted.push(claim);
  }

  const safetyRejected=rejected.some(item=>
    ['safety_requires_validated_knowledge','safety_evidence_invalid'].includes(item.reason)
  );
  return Object.freeze({
    accepted:Object.freeze(accepted),
    rejected:Object.freeze(rejected),
    fallback:safetyRejected?safetyKnowledgeFallback():null,
    canPresent:safetyRejected===false&&rejected.length===0
  });
}

export function dymekEvidenceSummary(result={}){
  const counts={
    observed:0,
    validated_knowledge:0,
    derived_calculation:0,
    general_suggestion:0
  };
  for(const claim of result.accepted||[]){
    if(Object.hasOwn(counts,claim.evidenceClass))counts[claim.evidenceClass]+=1;
  }
  return Object.freeze({
    claimCount:(result.accepted||[]).length,
    rejectedCount:(result.rejected||[]).length,
    safetyFallback:Boolean(result.fallback),
    evidenceClassCounts:Object.freeze(counts)
  });
}
